import React, { useState, useRef } from 'react';
import { X } from 'lucide-react';
import FormattingToolbar from '../FormattingToolbar';

const QuestionForm = ({ initialData = {}, onSubmit, onCancel }) => {
  const [title, setTitle] = useState(initialData.title || '');
  const [content, setContent] = useState(initialData.content || '');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const contentRef = useRef(null);
  
  const validate = () => {
    const newErrors = {};
    
    if (!title.trim()) {
      newErrors.title = 'Title is required';
    } else if (title.length < 10) {
      newErrors.title = 'Title must be at least 10 characters';
    } else if (title.length > 150) {
      newErrors.title = 'Title must be less than 150 characters';
    }
    
    if (!content.trim()) {
      newErrors.content = 'Question details are required';
    } else if (content.length < 20) {
      newErrors.content = 'Question details must be at least 20 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }
    
    setSubmitting(true);
    
    try {
      await onSubmit({ title, content });
      
      // Reset form if not updating
      if (!initialData.id) {
        setTitle('');
        setContent('');
      }
    } catch (error) {
      console.error('Error submitting question:', error);
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="question-title" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Title
        </label>
        <input
          type="text"
          id="question-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="What's your question? Be specific."
          className={`mt-1 block w-full rounded-md ${
            errors.title 
              ? 'border-red-300 dark:border-red-500 focus:ring-red-500 focus:border-red-500' 
              : 'border-gray-300 dark:border-gray-500 focus:ring-red-500 focus:border-red-500'
          } shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400`}
        />
        {errors.title && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.title}</p>
        )}
      </div>
      
      <div>
        <label htmlFor="question-content" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Details
        </label>
        <div className="mt-1">
          <FormattingToolbar onFormat={setContent} textRef={contentRef} />
          <textarea
            ref={contentRef}
            id="question-content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows="6"
            placeholder="Include all information someone would need to answer your question. You can use Markdown formatting."
            className={`block w-full rounded-b-md border-t-0 ${
              errors.content 
                ? 'border-red-300 dark:border-red-500 focus:ring-red-500 focus:border-red-500' 
                : 'border-gray-300 dark:border-gray-500 focus:ring-red-500 focus:border-red-500'
            } shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400`}
          />
        </div>
        {errors.content && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.content}</p>
        )}
      </div>
      
      <div className="flex justify-end space-x-2">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        >
          <X className="mr-2 -ml-1 h-5 w-5" />
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${
            submitting ? 'opacity-75 cursor-not-allowed' : ''
          }`}
        >
          {submitting ? (
            <>
              <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2"></div>
              {initialData.id ? 'Updating...' : 'Post Question'}
            </>
          ) : (
            initialData.id ? 'Update Question' : 'Post Question'
          )}
        </button>
      </div>
      
      <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
        <p>Tips for a good question:</p>
        <ul className="list-disc list-inside ml-2 mt-1">
          <li>Make sure your question is clear and concise</li>
          <li>Provide context and any relevant details</li>
          <li>Check if your question has been asked before</li>
        </ul>
      </div>
    </form>
  );
};

export default QuestionForm;